import {
  LocalNotifications,
  LocalNotificationSchema,
  ScheduleOptions,
  ActionPerformed,
  PendingLocalNotificationSchema
} from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { storage } from '../utils/storage';
import AlarmKit from './alarmKit';

// Reminder options in minutes
export const REMINDER_OPTIONS = [
  { label: 'At start time', value: 0 },
  { label: '5 minutes before', value: 5 },
  { label: '10 minutes before', value: 10 },
  { label: '15 minutes before', value: 15 },
  { label: '30 minutes before', value: 30 },
  { label: '1 hour before', value: 60 },
  { label: '2 hours before', value: 120 },
  { label: '1 day before', value: 1440 },
];

// Storage key for reminders
const REMINDERS_STORAGE_KEY = 'activity_reminders';

// AlarmKit has a limited number of concurrent alarm slots (~4)
const MAX_ALARMKIT_ALARMS = 4;

// Debug log for alarm scheduling - collected and shown as single alert
let _alarmDebugLog: string[] = [];

// Interface for stored reminder
export interface ActivityReminder {
  activityUuid: string;
  activityName: string;
  startDate: string; // ISO date string
  startTime: string; // HH:MM format
  endTime?: string; // HH:MM format
  minutesBefore: number;
  reminderTarget?: 'start' | 'end' | 'both'; // Which time(s) to remind for (defaults to 'start')
  notificationId: number; // Numeric ID for local notification
  endNotificationId?: number; // Second notification ID for 'both' target
  alarmId?: string; // UUID for AlarmKit alarm
  endAlarmId?: string; // Second AlarmKit alarm ID for 'both' target
  useAlarmKit?: boolean; // Whether this uses AlarmKit
  childName?: string;
  location?: string;
}

class LocalNotificationService {
  private static instance: LocalNotificationService;
  private isInitialized: boolean = false;
  private notificationIdCounter: number = 1000; // Start from 1000 to avoid conflicts

  private constructor() {}

  public static getInstance(): LocalNotificationService {
    if (!LocalNotificationService.instance) {
      LocalNotificationService.instance = new LocalNotificationService();
    }
    return LocalNotificationService.instance;
  }

  /**
   * Get and clear the alarm debug log. Call after sync to show a single summary alert.
   */
  public flushDebugLog(): string[] {
    const log = [..._alarmDebugLog];
    _alarmDebugLog = [];
    return log;
  }

  /**
   * Initialize local notifications
   */
  public async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.log('🔔 Local notifications only available on native platforms');
      return;
    }

    if (this.isInitialized) {
      console.log('🔔 Local notifications already initialized');
      return;
    }

    try {
      console.log('🔔 Initializing local notifications...');

      // Request permissions
      const permStatus = await LocalNotifications.requestPermissions();
      console.log('🔔 Permission status:', permStatus);

      if (permStatus.display !== 'granted') {
        console.warn('⚠️ Local notification permission denied');
        return;
      }

      // Set up listeners
      await LocalNotifications.addListener('localNotificationReceived',
        (notification: LocalNotificationSchema) => {
          console.log('🔔 Local notification received:', notification);
          this.handleNotificationReceived(notification);
        }
      );

      await LocalNotifications.addListener('localNotificationActionPerformed',
        (action: ActionPerformed) => {
          console.log('🔔 Local notification action:', action);
          this.handleNotificationAction(action);
        }
      );

      // Load the notification ID counter from storage
      await this.loadNotificationIdCounter();

      this.isInitialized = true;
      console.log('✅ Local notifications initialized');

    } catch (error) {
      console.error('❌ Failed to initialize local notifications:', error);
    }
  }

  /**
   * Request AlarmKit permission proactively (must be called from user interaction)
   * This ensures the iOS permission dialog appears immediately in response to a tap
   */
  public async requestAlarmPermission(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      return false;
    }
    try {
      const permResult = await AlarmKit.requestPermission();
      console.log('🔔 AlarmKit permission result:', permResult);
      return permResult.granted;
    } catch (error) {
      console.log('⚠️ AlarmKit not available:', error);
      // Fall back to local notification permissions
      const permStatus = await LocalNotifications.requestPermissions();
      return permStatus.display === 'granted';
    }
  }

  /**
   * Check if AlarmKit permission is granted without prompting the user
   * Returns: { available: boolean, granted: boolean }
   */
  public async checkAlarmPermission(): Promise<{ available: boolean; granted: boolean }> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      return { available: false, granted: false };
    }
    try {
      const availability = await AlarmKit.isAvailable();
      if (!availability.available) {
        return { available: false, granted: false };
      }
      const permResult = await AlarmKit.checkPermission();
      return { available: true, granted: permResult.granted };
    } catch (error) {
      return { available: false, granted: false };
    }
  }

  /**
   * Load notification ID counter from storage
   */
  private async loadNotificationIdCounter(): Promise<void> {
    const stored = await storage.getItem('notification_id_counter');
    if (stored) {
      this.notificationIdCounter = parseInt(stored, 10) || 1000;
    }
  }

  /**
   * Save notification ID counter to storage
   */
  private async saveNotificationIdCounter(): Promise<void> {
    await storage.setItem('notification_id_counter', this.notificationIdCounter.toString());
  }

  /**
   * Get next notification ID
   */
  private async getNextNotificationId(): Promise<number> {
    this.notificationIdCounter++;
    await this.saveNotificationIdCounter();
    return this.notificationIdCounter;
  }

  /**
   * Handle notification received while app is open
   */
  private handleNotificationReceived(notification: LocalNotificationSchema): void {
    const event = new CustomEvent('localNotificationReceived', {
      detail: notification
    });
    window.dispatchEvent(event);
  }

  /**
   * Handle notification tap/action
   */
  private handleNotificationAction(action: ActionPerformed): void {
    const notification = action.notification;
    const event = new CustomEvent('localNotificationAction', {
      detail: {
        notification,
        actionId: action.actionId
      }
    });
    window.dispatchEvent(event);
  }

  /**
   * Build notification title and body for a reminder
   * Format: "ChildName: ActivityName in Xm" or "ActivityName in Xm"
   */
  private buildNotificationText(
    activityName: string,
    childName: string | undefined,
    minutesBefore: number,
    isEndTime: boolean
  ): { title: string; body: string } {
    const timeLabel = isEndTime ? 'ends' : 'starts';
    const shortMinutes = this.formatMinutesShort(minutesBefore);

    // Title: "ChildName: ActivityName in 30m" or "ActivityName in 30m"
    let title: string;
    if (childName) {
      if (minutesBefore > 0) {
        title = `${childName}: ${activityName} ${timeLabel} in ${shortMinutes}`;
      } else {
        title = `${childName}: ${activityName} ${timeLabel} now!`;
      }
    } else {
      if (minutesBefore > 0) {
        title = `${activityName} ${timeLabel} in ${shortMinutes}`;
      } else {
        title = `${activityName} ${timeLabel} now!`;
      }
    }

    // Body is the same as title for alarm display
    return { title, body: title };
  }

  /**
   * Format minutes into short readable string (e.g. "30m", "1h", "2h", "1d")
   */
  private formatMinutesShort(minutes: number): string {
    if (minutes < 60) {
      return `${minutes}m`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      return `${hours}h`;
    } else {
      const days = Math.floor(minutes / 1440);
      return `${days}d`;
    }
  }

  /**
   * Count how many AlarmKit alarms are actually pending in the OS.
   * Falls back to storage-based count if the native query fails.
   */
  private async getActiveAlarmKitCount(): Promise<number> {
    // Ask AlarmKit directly — most accurate, accounts for fired/expired alarms
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const result = await AlarmKit.getScheduledAlarms();
        const pending = result.alarms.filter(a => a.state === 'scheduled' || a.state === 'pending');
        return pending.length;
      } catch (e) {
        // Fall through to storage-based count
      }
    }

    // Fallback: count from storage using alarm trigger time (not event time)
    const reminders = await this.getAllReminders();
    const now = new Date();
    return reminders.filter(r => {
      if (!r.useAlarmKit) return false;
      const eventDate = this.parseActivityDateTime(r.startDate, r.startTime);
      if (!eventDate) return false;
      // Use actual trigger time (event time minus minutesBefore)
      const triggerDate = new Date(eventDate.getTime() - (r.minutesBefore || 0) * 60000);
      return triggerDate > now;
    }).length;
  }

  /**
   * Schedule a single alarm (AlarmKit or LocalNotification fallback)
   * Returns { alarmId, notificationId, useAlarmKit } or null if failed
   */
  private async scheduleSingleAlarm(
    activityUuid: string,
    title: string,
    body: string,
    notificationTime: Date,
    forceLocalOnly: boolean = false
  ): Promise<{ alarmId?: string; notificationId: number; useAlarmKit: boolean } | null> {
    // Don't schedule if the notification time is in the past
    if (notificationTime <= new Date()) {
      _alarmDebugLog.push(`⏭️ "${title}" | ${notificationTime.toLocaleDateString()} ${notificationTime.toLocaleTimeString()} | SKIPPED (in the past)`);
      return null;
    }

    // Try AlarmKit first (iOS 26.1+, within 24 hours only - timer duration limit)
    const secsUntil = Math.round((notificationTime.getTime() - Date.now()) / 1000);
    if (Capacitor.getPlatform() === 'ios' && secsUntil <= 86400) {
      try {
        // Check if we have AlarmKit slots available (limit of ~4 concurrent alarms)
        const activeCount = await this.getActiveAlarmKitCount();
        if (activeCount >= MAX_ALARMKIT_ALARMS) {
          _alarmDebugLog.push(`⏸️ "${title}" | ${notificationTime.toLocaleDateString()} ${notificationTime.toLocaleTimeString()} | FULL ${activeCount}/${MAX_ALARMKIT_ALARMS} → local notif`);
        } else {
          // Check availability first (iOS version check) before calling checkPermission
          const availability = await AlarmKit.isAvailable();
          if (availability.available) {
            const permResult = await AlarmKit.checkPermission();
            if (permResult.granted) {
              const alarmId = crypto.randomUUID();
              console.log('🔔 Scheduling AlarmKit alarm for:', notificationTime.toISOString(), 'title:', title);
              const result = await AlarmKit.scheduleAlarm({
                id: alarmId,
                title,
                body,
                triggerTime: notificationTime.getTime(),
                activityUuid
              });

              if (result.success) {
                _alarmDebugLog.push(`✅ "${title}" | ${notificationTime.toLocaleDateString()} ${notificationTime.toLocaleTimeString()} | slot ${activeCount + 1}/${MAX_ALARMKIT_ALARMS} | trigger: ${notificationTime.getTime()}`);
                return { alarmId, notificationId: 0, useAlarmKit: true };
              } else {
                _alarmDebugLog.push(`⚠️ "${title}" | success=false | ${notificationTime.toLocaleDateString()} ${notificationTime.toLocaleTimeString()}`);
              }
            } else {
              _alarmDebugLog.push(`🔒 Permission denied (${permResult.status})`);
            }
          } else {
            _alarmDebugLog.push(`📵 AlarmKit not available`);
          }
        }
      } catch (alarmKitError: any) {
        _alarmDebugLog.push(`❌ "${title}" | ${notificationTime.toLocaleDateString()} ${notificationTime.toLocaleTimeString()} | in ${secsUntil}s | slots: ${await this.getActiveAlarmKitCount()}/${MAX_ALARMKIT_ALARMS} | ${alarmKitError?.message || alarmKitError}`);
      }
    } else if (Capacitor.getPlatform() === 'ios' && secsUntil > 86400) {
      _alarmDebugLog.push(`⏳ "${title}" | ${notificationTime.toLocaleDateString()} ${notificationTime.toLocaleTimeString()} | in ${Math.round(secsUntil/3600)}h → local notif (>24h, bg task will promote later)`);
    }

    // Fallback to local notifications
    console.log('🔔 Using LOCAL NOTIFICATION fallback (not AlarmKit alarm) for:', title);
    const notificationId = await this.getNextNotificationId();
    const scheduleOptions: ScheduleOptions = {
      notifications: [
        {
          id: notificationId,
          title: `⏰ ${title}`,
          body,
          schedule: {
            at: notificationTime,
            allowWhileIdle: true
          },
          sound: 'alarm.wav',
          extra: {
            activityUuid,
            type: 'activity_reminder'
          }
        }
      ]
    };

    await LocalNotifications.schedule(scheduleOptions);
    console.log('✅ Local notification scheduled for:', notificationTime.toISOString());
    return { notificationId, useAlarmKit: false };
  }

  /**
   * Schedule a reminder for an activity
   * Supports start time, end time, or both based on reminderTarget
   */
  public async scheduleReminder(reminder: Omit<ActivityReminder, 'notificationId'>): Promise<ActivityReminder | null> {
    if (!Capacitor.isNativePlatform()) {
      console.log('🔔 Reminder not scheduled - alarms only sound on iOS app:', reminder.activityName);
      return null;
    }

    try {
      const target = reminder.reminderTarget || 'start';
      const fullReminder: ActivityReminder = {
        ...reminder,
        reminderTarget: target,
        notificationId: 0,
        useAlarmKit: false
      };

      // Schedule start time alarm
      if (target === 'start' || target === 'both') {
        const startDateTime = this.parseActivityDateTime(reminder.startDate, reminder.startTime);
        if (!startDateTime) {
          console.error('❌ Invalid activity start date/time');
          return null;
        }

        const startNotificationTime = new Date(startDateTime.getTime() - reminder.minutesBefore * 60 * 1000);
        const { title, body } = this.buildNotificationText(
          reminder.activityName,
          reminder.childName,
          reminder.minutesBefore,
          false
        );

        const startResult = await this.scheduleSingleAlarm(
          reminder.activityUuid,
          title,
          body,
          startNotificationTime
        );

        if (startResult) {
          fullReminder.notificationId = startResult.notificationId;
          fullReminder.alarmId = startResult.alarmId;
          fullReminder.useAlarmKit = startResult.useAlarmKit;
        }
      }

      // Schedule end time alarm
      if ((target === 'end' || target === 'both') && reminder.endTime) {
        const endDateTime = this.parseActivityDateTime(reminder.startDate, reminder.endTime);
        if (!endDateTime) {
          console.error('❌ Invalid activity end date/time');
          if (target === 'end') return null;
        } else {
          const endNotificationTime = new Date(endDateTime.getTime() - reminder.minutesBefore * 60 * 1000);
          const { title, body } = this.buildNotificationText(
            reminder.activityName,
            reminder.childName,
            reminder.minutesBefore,
            true
          );

          const endResult = await this.scheduleSingleAlarm(
            `${reminder.activityUuid}_end`,
            title,
            body,
            endNotificationTime
          );

          if (endResult) {
            fullReminder.endNotificationId = endResult.notificationId;
            fullReminder.endAlarmId = endResult.alarmId;
          }
        }
      }

      await this.saveReminder(fullReminder);

      // Flush debug log (consumed silently)
      this.flushDebugLog();

      return fullReminder;

    } catch (error) {
      console.error('❌ Failed to schedule reminder:', error);
      return null;
    }
  }

  /**
   * Cancel a reminder for an activity
   */
  public async cancelReminder(activityUuid: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      const reminder = await this.getReminder(activityUuid);
      if (reminder) {
        // Cancel start alarm
        if (reminder.useAlarmKit && reminder.alarmId) {
          try {
            await AlarmKit.cancelAlarm({ id: reminder.alarmId });
            console.log('✅ AlarmKit alarm cancelled for activity:', activityUuid);
          } catch (alarmKitError) {
            console.log('⚠️ Failed to cancel AlarmKit alarm:', alarmKitError);
          }
        } else if (reminder.notificationId) {
          await LocalNotifications.cancel({
            notifications: [{ id: reminder.notificationId }]
          });
          console.log('✅ Local notification cancelled for activity:', activityUuid);
        }

        // Cancel end alarm if exists (for 'both' or 'end' target)
        if (reminder.endAlarmId) {
          try {
            await AlarmKit.cancelAlarm({ id: reminder.endAlarmId });
            console.log('✅ AlarmKit end alarm cancelled for activity:', activityUuid);
          } catch (alarmKitError) {
            console.log('⚠️ Failed to cancel AlarmKit end alarm:', alarmKitError);
          }
        }
        if (reminder.endNotificationId) {
          await LocalNotifications.cancel({
            notifications: [{ id: reminder.endNotificationId }]
          });
          console.log('✅ Local end notification cancelled for activity:', activityUuid);
        }

        await this.removeReminder(activityUuid);
      }
    } catch (error) {
      console.error('❌ Failed to cancel reminder:', error);
    }
  }

  /**
   * Get all scheduled reminders
   */
  public async getAllReminders(): Promise<ActivityReminder[]> {
    const stored = await storage.getItem(REMINDERS_STORAGE_KEY);
    if (!stored) {
      return [];
    }
    try {
      return JSON.parse(stored) as ActivityReminder[];
    } catch {
      return [];
    }
  }

  /**
   * Get reminder for a specific activity
   */
  public async getReminder(activityUuid: string): Promise<ActivityReminder | null> {
    const reminders = await this.getAllReminders();
    return reminders.find(r => r.activityUuid === activityUuid) || null;
  }

  /**
   * Save reminder to storage
   */
  private async saveReminder(reminder: ActivityReminder): Promise<void> {
    const reminders = await this.getAllReminders();
    // Remove existing reminder for same activity
    const filtered = reminders.filter(r => r.activityUuid !== reminder.activityUuid);
    filtered.push(reminder);
    await storage.setItem(REMINDERS_STORAGE_KEY, JSON.stringify(filtered));
  }

  /**
   * Remove reminder from storage
   */
  private async removeReminder(activityUuid: string): Promise<void> {
    const reminders = await this.getAllReminders();
    const filtered = reminders.filter(r => r.activityUuid !== activityUuid);
    await storage.setItem(REMINDERS_STORAGE_KEY, JSON.stringify(filtered));
  }

  /**
   * Clear all stored reminders (e.g. after app reinstall when OS alarms are gone)
   */
  public async clearAllReminders(): Promise<void> {
    await storage.setItem(REMINDERS_STORAGE_KEY, JSON.stringify([]));
  }

  /**
   * Purge ALL pending OS alarms/notifications and clear storage.
   * Use this to clean up stale duplicate alarms from before dedup fix.
   */
  public async purgeAllAlarms(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
      // Cancel all pending local notifications
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({ notifications: pending.notifications.map(n => ({ id: n.id })) });
        console.log(`🧹 Purged ${pending.notifications.length} pending local notifications`);
      }

      // Cancel all stored AlarmKit alarms by their saved IDs
      if (Capacitor.getPlatform() === 'ios') {
        try {
          const storedReminders = await this.getAllReminders();
          const alarmIds: string[] = [];
          for (const r of storedReminders) {
            if (r.alarmId) alarmIds.push(r.alarmId);
            if (r.endAlarmId) alarmIds.push(r.endAlarmId);
          }
          if (alarmIds.length > 0) {
            console.log(`🧹 Cancelling ${alarmIds.length} stored AlarmKit alarms:`, alarmIds);
            const result = await AlarmKit.cancelAlarms({ ids: alarmIds });
            console.log(`🧹 Cancelled ${result.cancelled} AlarmKit alarms`);
          } else {
            console.log('🧹 No stored AlarmKit alarm IDs to cancel');
          }
        } catch (e) {
          console.log('🧹 AlarmKit cancel failed (may not be available):', e);
        }
      }

      // Clear storage
      await storage.setItem(REMINDERS_STORAGE_KEY, JSON.stringify([]));
      console.log('🧹 Cleared all stored reminders');
    } catch (error) {
      console.error('Failed to purge alarms:', error);
    }
  }

  /**
   * Parse activity date and time into a Date object
   */
  private parseActivityDateTime(dateStr: string, timeStr: string): Date | null {
    try {
      // Date format: YYYY-MM-DD
      const datePart = dateStr.split('T')[0]; // Handle ISO strings

      // Time format: HH:MM or HH:MM:SS
      let hours = 0;
      let minutes = 0;

      if (timeStr) {
        const timeParts = timeStr.split(':');
        hours = parseInt(timeParts[0], 10);
        minutes = parseInt(timeParts[1], 10);
      }

      const [year, month, day] = datePart.split('-').map(n => parseInt(n, 10));
      return new Date(year, month - 1, day, hours, minutes, 0, 0);
    } catch {
      return null;
    }
  }

  /**
   * Format minutes into readable string
   */
  private formatMinutes(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    } else {
      const days = Math.floor(minutes / 1440);
      return `${days} day${days !== 1 ? 's' : ''}`;
    }
  }

  /**
   * Get all pending notifications from the system
   */
  public async getPendingNotifications(): Promise<PendingLocalNotificationSchema[]> {
    if (!Capacitor.isNativePlatform()) {
      return [];
    }

    const result = await LocalNotifications.getPending();
    return result.notifications;
  }

  /**
   * Clean up expired reminders from storage
   */
  public async cleanupExpiredReminders(): Promise<void> {
    const reminders = await this.getAllReminders();
    const now = new Date();

    const validReminders = reminders.filter(reminder => {
      const activityDateTime = this.parseActivityDateTime(reminder.startDate, reminder.startTime);
      return activityDateTime && activityDateTime > now;
    });

    if (validReminders.length !== reminders.length) {
      await storage.setItem(REMINDERS_STORAGE_KEY, JSON.stringify(validReminders));
      console.log(`🔔 Cleaned up ${reminders.length - validReminders.length} expired reminders`);
    }
  }

  /**
   * Unregister and cleanup
   */
  public async unregister(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await LocalNotifications.removeAllListeners();
      this.isInitialized = false;
      console.log('✅ Local notifications unregistered');
    } catch (error) {
      console.error('❌ Error unregistering local notifications:', error);
    }
  }
}

export default LocalNotificationService;
